import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
// Seam API imports removed — Seam integration disabled

// Stub functions to prevent runtime errors
const listAccessCodes = async (deviceId: string) => [];
const createAccessCode = async (deviceId: string, name: string, code?: string, startsAt?: string, endsAt?: string) => null;
const deleteAccessCode = async (accessCodeId: string) => null;

interface Props {
  deviceId: string;
}

export const AccessCodeManager: React.FC<Props> = ({ deviceId }) => {
  const [codes, setCodes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [isTimeBound, setIsTimeBound] = useState(false);
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const result = await listAccessCodes(deviceId);
      setCodes(result);
    } catch (e: any) {
      console.error('Failed to load access codes:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCodes(); }, [deviceId]);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    setError('');
    try {
      await createAccessCode(
        deviceId,
        name.trim(),
        code || undefined,
        isTimeBound ? startsAt : undefined,
        isTimeBound ? endsAt : undefined
      );
      setShowCreate(false);
      setName('');
      setCode('');
      setIsTimeBound(false);
      setStartsAt('');
      setEndsAt('');
      fetchCodes();
    } catch (e: any) {
      setError(e.message || 'Failed to create access code');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (accessCodeId: string) => {
    if (!window.confirm('Delete this access code?')) return;
    setDeleting(accessCodeId);
    try {
      await deleteAccessCode(accessCodeId);
      fetchCodes();
    } catch (e: any) {
      console.error('Failed to delete access code:', e);
    } finally {
      setDeleting(null);
    }
  };

  if (loading) return <div className="text-center py-4 text-gray-400">Loading access codes...</div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-gray-700">{codes.length} access code{codes.length !== 1 ? 's' : ''}</p>
        <Button size="sm" onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? 'Cancel' : '+ New Code'}
        </Button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">{error}</div>}

      {showCreate && (
        <div className="border border-emerald-200 rounded-lg p-4 bg-emerald-50 space-y-3">
          <Input label="Name" placeholder="e.g., Front Door Guest" value={name} onChange={(e: any) => setName(e.target.value)} />
          <Input label="PIN Code (optional — auto-generated if blank)" placeholder="e.g., 1234" value={code} onChange={(e: any) => setCode(e.target.value)} />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isTimeBound} onChange={(e) => setIsTimeBound(e.target.checked)} className="rounded text-brand-600" />
            Time-limited access
          </label>
          {isTimeBound && (
            <div className="grid grid-cols-2 gap-3">
              <Input label="Starts" type="datetime-local" value={startsAt} onChange={(e: any) => setStartsAt(e.target.value)} />
              <Input label="Ends" type="datetime-local" value={endsAt} onChange={(e: any) => setEndsAt(e.target.value)} />
            </div>
          )}
          <div className="flex justify-end">
            <Button size="sm" onClick={handleCreate} loading={creating} disabled={!name.trim()}>Create Code</Button>
          </div>
        </div>
      )}

      {codes.length === 0 && !showCreate ? (
        <div className="text-center py-6 text-gray-400 text-sm">No access codes set up yet.</div>
      ) : (
        <div className="space-y-2">
          {codes.map((ac: any) => (
            <div key={ac.access_code_id} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-white">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{ac.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs font-mono text-gray-600">{ac.code || '****'}</span>
                  <Badge color={ac.type === 'time_bound' ? 'yellow' : 'green'}>
                    {ac.type === 'time_bound' ? 'Scheduled' : 'Ongoing'}
                  </Badge>
                  <Badge color={ac.status === 'set' ? 'green' : ac.status === 'setting' ? 'yellow' : 'gray'}>
                    {ac.status}
                  </Badge>
                </div>
                {ac.type === 'time_bound' && ac.starts_at && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    {new Date(ac.starts_at).toLocaleString()} — {new Date(ac.ends_at).toLocaleString()}
                  </p>
                )}
              </div>
              <button
                onClick={() => handleDelete(ac.access_code_id)}
                disabled={deleting === ac.access_code_id}
                className="text-xs text-red-500 hover:text-red-700 font-medium ml-3"
              >
                {deleting === ac.access_code_id ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
